import React from "react"
import {
  referralStatusLabel,
  referralStatusTone,
  type ReferralDiscountRow,
} from "./referralDiscountUtils.ts"

type InvoiceReferralDiscountEditorProps = {
  rows: ReferralDiscountRow[]
  onChange: (rows: ReferralDiscountRow[]) => void
  isLoading?: boolean
}

export function InvoiceReferralDiscountEditor({
  rows,
  onChange,
  isLoading = false,
}: InvoiceReferralDiscountEditorProps) {
  if (!rows.length && !isLoading) return null

  const updateRow = (referredCustomerId: string, patch: Partial<ReferralDiscountRow>) => {
    onChange(
      rows.map((row) =>
        row.referred_customer_id === referredCustomerId ? { ...row, ...patch } : row,
      ),
    )
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm space-y-3">
      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Referral discounts</h3>
          <p className="text-xs text-slate-500 mt-0.5">500 PKR per eligible referred customer on subscription invoices.</p>
        </div>
        {isLoading ? <span className="text-xs text-slate-400">Loading…</span> : null}
      </div>

      {!rows.length && !isLoading ? (
        <p className="text-sm text-slate-500">No referred customers for this account.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => {
            const removed = row.removed || row.eligibility_status === "removed_manual"
            const editable = row.eligibility_status === "eligible" && !removed
            return (
              <div
                key={row.referred_customer_id}
                className="rounded-md border border-slate-200 bg-slate-50/70 p-3 space-y-2"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {row.referred_customer_name || "Referred customer"}
                    </p>
                    <p className="text-xs text-slate-500 font-mono">{row.referred_internet_id}</p>
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${referralStatusTone(
                      removed ? "removed_manual" : row.eligibility_status,
                    )}`}
                  >
                    {referralStatusLabel(removed ? "removed_manual" : row.eligibility_status)}
                  </span>
                </div>

                {row.source_note && row.eligibility_status !== "eligible" ? (
                  <p className="text-xs text-slate-500">{row.source_note}</p>
                ) : null}

                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-28">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600 mb-1">
                      Amount
                    </label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={!editable}
                      value={editable ? row.applied_discount_amount : 0}
                      onChange={(e) =>
                        updateRow(row.referred_customer_id, {
                          applied_discount_amount: Math.max(0, Number(e.target.value) || 0),
                        })
                      }
                      className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md bg-white text-slate-800 disabled:bg-slate-100 disabled:text-slate-400"
                    />
                  </div>
                  {row.eligibility_status === "eligible" ? (
                    <button
                      type="button"
                      onClick={() =>
                        updateRow(row.referred_customer_id, {
                          removed: !removed,
                          applied_discount_amount: removed ? row.base_discount_amount : 0,
                        })
                      }
                      className="h-9 px-3 text-xs font-medium rounded-md border border-slate-200 text-slate-600 hover:bg-white transition-colors"
                    >
                      {removed ? "Restore" : "Remove"}
                    </button>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
